import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private getBaseUrl(): string {
    return `${window.location.protocol}//${window.location.host}`;
  }

  updateSeoTags(url: string, pageTitle?: string, description?: string): void {
    // Update title if provided
    if (pageTitle) {
      this.title.setTitle(pageTitle);
    }

    // Update description if provided
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
    }

    // Update canonical URL
    this.updateCanonicalUrl(url);
    
    // Update hreflang tags
    this.updateHreflangTags(url);
  }

  private updateCanonicalUrl(url: string): void {
    const baseUrl = this.getBaseUrl();
    const canonicalUrl = baseUrl + url;
    
    const existingCanonical = this.document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.setAttribute('href', canonicalUrl);
    } else {
      const canonicalLink = this.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      this.document.head.appendChild(canonicalLink);
    }
  }

  private updateHreflangTags(url: string): void {
    const baseUrl = this.getBaseUrl();
    const currentUrl = baseUrl + url;
    
    const existingHreflang = this.document.querySelector('link[rel="alternate"][hreflang="en-in"]');
    if (existingHreflang) {
      existingHreflang.setAttribute('href', currentUrl);
    } else {
      const hreflangLink = this.document.createElement('link');
      hreflangLink.setAttribute('rel', 'alternate');
      hreflangLink.setAttribute('hreflang', 'en-in');
      hreflangLink.setAttribute('href', currentUrl);
      this.document.head.appendChild(hreflangLink);
    }
  }

  addJsonLdSchema(schema: object, id: string = 'jsonld-schema'): void {
    const existing = this.document.getElementById(id);
    if (existing) {
      existing.remove();
    }
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  addBreadcrumbJsonLd(items: Array<{ name: string; item: string }>, id: string = 'jsonld-breadcrumb'): void {
    this.removeExistingBreadcrumbJsonLd();
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    const baseDomain = 'https://varrocconnect.com';
    script.text = JSON.stringify({
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${baseDomain}/`
        },
        ...items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 2,
          name: item.name,
          item: item.item
        }))
      ]
    });
    this.document.head.appendChild(script);
  }

  private removeExistingBreadcrumbJsonLd(): void {
    const scripts = Array.from(this.document.head.querySelectorAll('script[type="application/ld+json"]')) as HTMLScriptElement[];
    scripts.forEach(script => {
      try {
        const payload = JSON.parse(script.text || '');
        if (payload && payload['@type'] === 'BreadcrumbList') {
          script.remove();
        }
      } catch {
        // ignore invalid JSON
      }
    });
  }

  // Optional: Method to add multiple hreflang tags for different locales
  updateMultipleHreflangTags(url: string, locales: { [key: string]: string } = { 'en-in': url }): void {
    const baseUrl = this.getBaseUrl();
    
    // Remove existing hreflang links
    const existingHreflang = this.document.querySelectorAll('link[rel="alternate"]');
    existingHreflang.forEach(link => link.remove());
    
    // Add new hreflang links for each locale
    Object.entries(locales).forEach(([locale, localizedUrl]) => {
      const hreflangLink = this.document.createElement('link');
      hreflangLink.setAttribute('rel', 'alternate');
      hreflangLink.setAttribute('hreflang', locale);
      hreflangLink.setAttribute('href', baseUrl + localizedUrl);
      this.document.head.appendChild(hreflangLink);
    });
  }
}